/**
 * Shared browser-side Logger factory.
 *
 * Callers supply a transport (a function that ships one `BrowserLogEntry`
 * to their BE endpoint) plus static context (service, version, session,
 * logger sub-tag) and get back a Logger with the same shape circuit-app's
 * existing Logger uses:
 *
 *   logger.info({ message, context? })
 *   logger.warn({ message, context? })
 *   logger.error({ message, error?, context? })
 *   logger.debug({ message, context? })
 *   logger.fatal({ message, error?, context? })
 *
 * The factory handles:
 *   - Envelope construction (`{ level, status, date, message, metadata }`)
 *   - `error.*` population for DD Error Tracking auto-fingerprinting.
 *   - Redaction of the whole metadata block before ship.
 *   - Per-log X-Request-Id mint (or accept a caller-supplied one).
 *
 * Callers own the transport, so this stays isomorphic — circuit-app can
 * pipe to `/api/logs` batching + pino; circuit-chat-embed can pipe to its
 * `httpClient` beacon; a hypothetical server-side surface could pipe
 * straight to stdout.
 */

import type {
  BrowserLogEntry,
  ErrorMeta,
  EventMeta,
  LogMetadata,
  UsrMeta,
} from './types';

import { generateSafeUUID } from '../id/generate-safe-uuid';
import { formatError, type FormattedError } from './format-error';

/**
 * Caller-supplied entry data. Everything is optional except `message` so
 * the wrappers on the returned Logger can be as terse as
 *   logger.info({ message: 'foo' })
 */
export interface LogEntryInput {
  context?: Record<string, unknown>;
  event?: EventMeta;
  message: string;
  requestId?: string;
}

export interface LogErrorEntryInput extends LogEntryInput {
  error?: unknown;
}

/**
 * The `Logger` shape returned by `createBrowserLogger`. Mirrors
 * circuit-app's existing Logger surface exactly so the two frontends can
 * share call-site code.
 */
export interface BrowserLogger {
  debug: (entry: LogEntryInput) => string;
  error: (entry: LogErrorEntryInput) => string;
  fatal: (entry: LogErrorEntryInput) => string;
  info: (entry: LogEntryInput) => string;
  warn: (entry: LogEntryInput) => string;
}

/**
 * Static context that lands on every log entry the factory emits.
 */
export interface BrowserLoggerContext {
  /**
   * Extra static fields to attach to every log's metadata. Useful for
   * things like `env: 'prod'` when the BE isn't already stamping it.
   */
  extraMetadata?: Record<string, unknown>;
  /**
   * Optional metadata redactor. Callers that carry user-input into logs
   * should pass `redactSensitiveFields` from this package to strip
   * tokens/passwords before ship. Omitted callers (e.g. the launcher IIFE)
   * ship metadata as-is — skipping the import keeps their bundle small.
   */
  redact?: (metadata: LogMetadata) => LogMetadata;

  /**
   * A function that returns the browser context (url, userAgent, ...) at
   * log time. Called on every log so a customer-page URL change gets
   * picked up. Callers pass a scrubbed URL.
   */
  getBrowserMeta?: () => LogMetadata['browser'];
  /**
   * A function that returns the current user context, if any. Called on
   * every log so a mid-session login gets picked up.
   */
  getUsrMeta?: () => UsrMeta | undefined;
  /** Sub-service tag (e.g. "spa", "launcher", "main"). */
  logger: string;
  /** Stable session id. Called once per log so tests can inject. */
  sessionId: () => string;
  /** Deploy version / commit sha. */
  version: string;
}

/**
 * Transport function: hand it one `BrowserLogEntry` and it ships. Return
 * value ignored (fire-and-forget). Must never throw — wrap in try/catch
 * if the underlying transport can throw synchronously.
 */
export type BrowserLogTransport = (entry: BrowserLogEntry) => void;

function toErrorMeta(formatted: FormattedError): ErrorMeta {
  const result: ErrorMeta = { message: formatted.message };
  if (formatted.stack !== undefined) result.stack = formatted.stack;
  if (formatted.name !== undefined) result.kind = formatted.name;
  return result;
}

function buildEntry(
  ctx: BrowserLoggerContext,
  transport: BrowserLogTransport,
  input: LogErrorEntryInput,
  level: BrowserLogEntry['level'],
): string {
  const callerContextRequestId = input.context?.['X-Request-Id'];
  const callerRequestId =
    typeof callerContextRequestId === 'string' && callerContextRequestId !== ''
      ? callerContextRequestId
      : undefined;
  // Priority: explicit requestId > context['X-Request-Id'] > fresh mint.
  const requestId = input.requestId ?? callerRequestId ?? generateSafeUUID();
  const formatted =
    input.error === undefined ? undefined : formatError(input.error);
  const metadata: LogMetadata = {
    ...ctx.extraMetadata,
    ...input.context,
    logger: ctx.logger,
    session: { id: ctx.sessionId() },
    version: ctx.version,
    'X-Request-Id': requestId,
  };
  const browser = ctx.getBrowserMeta?.();
  if (browser !== undefined) metadata.browser = browser;
  const usr = ctx.getUsrMeta?.();
  if (usr !== undefined) metadata.usr = usr;
  if (input.event !== undefined) metadata.event = input.event;
  if (formatted !== undefined) {
    metadata.error = toErrorMeta(formatted);
    // Populate `err` alongside `error` for circuit-app Logger back-compat.
    // eslint-disable-next-line id-denylist, @typescript-eslint/no-deprecated -- schema field name, intentional back-compat.
    metadata.err = formatted;
  }
  const entry: BrowserLogEntry = {
    date: new Date().toISOString(),
    level,
    message: input.message,
    metadata: ctx.redact ? ctx.redact(metadata) : metadata,
    status: level,
  };
  try {
    transport(entry);
  } catch {
    // Transport must swallow its own errors; this is defence in depth so a
    // buggy transport can't re-enter the caller's error handler.
  }
  return requestId;
}

/**
 * Build a Logger bound to a specific transport + static context.
 */
export function createBrowserLogger(
  transport: BrowserLogTransport,
  ctx: BrowserLoggerContext,
): BrowserLogger {
  return {
    debug: (input) => buildEntry(ctx, transport, input, 'debug'),
    error: (input) => buildEntry(ctx, transport, input, 'error'),
    fatal: (input) => buildEntry(ctx, transport, input, 'fatal'),
    info: (input) => buildEntry(ctx, transport, input, 'info'),
    warn: (input) => buildEntry(ctx, transport, input, 'warn'),
  };
}
